import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Layers, MapPin, ExternalLink } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { useImpactRecoveryObjects, useImpactRecoveryObjectStatus, useUpdateImpactRecoveryObjectStatus, useUpdateImpactRecoveryObject, useScenarioOptions, useImpactLocationOptions, useImpactTeamOptions } from '../../hooks'
import { useObject, useRolledUpRTO, useRolledUpRPO, useScenarios } from '@/features/objects/hooks'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { cn } from '@/lib/utils'

function ObjectStatusIndicator({ status }: { status: number | null }) {
    if (status === null || status === undefined) return <span className="text-muted-foreground">-</span>
    const config: Record<number, { color: string; ring: string }> = {
        4: { color: 'bg-green-500', ring: 'ring-green-500/20' },
        3: { color: 'bg-amber-500', ring: 'ring-amber-500/20' },
        2: { color: 'bg-red-500', ring: 'ring-red-500/20' },
        1: { color: 'bg-gray-500', ring: 'ring-gray-500/20' },
    }
    const c = config[status] ?? config[1]
    return <div className={cn('w-4 h-4 rounded-full ring-4 shadow-sm', c.color, c.ring)} />
}

interface Step5ImpactAssessmentProps {
    setId?: number
}

export function Step5ImpactAssessment({ setId }: Step5ImpactAssessmentProps) {
    const { t } = useTranslation()
    const [recoveryObjectsSearch, setRecoveryObjectsSearch] = useState('')
    const [statusSearch, setStatusSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | 'amber' | 'services' | 'amber-services'>('amber-services')
    const [scenarioId, setScenarioId] = useState<number | null>(null)
    const [locationId, setLocationId] = useState<number | null>(null)
    const [teamId, setTeamId] = useState<number | null>(null)
    const [editingIssueId, setEditingIssueId] = useState<number | null>(null)
    const [editingIssueValue, setEditingIssueValue] = useState('')
    const [selectedObjectID, setSelectedObjectID] = useState<string | null>(null)
    /** When modal is from Recovery Objects table use default scenario; when from Recovery Object Status use dropdown scenario */
    const [modalSource, setModalSource] = useState<'recovery-objects' | 'recovery-object-status' | null>(null)
    const [rtoBreakdownOpen, setRtoBreakdownOpen] = useState(false)
    const [rpoBreakdownOpen, setRpoBreakdownOpen] = useState(false)
    const issueEditRef = useRef<HTMLTextAreaElement>(null)
    const navigate = useNavigate()
    const { data: scenarioOptions = [] } = useScenarioOptions()
    const scenarioName = scenarioId != null ? scenarioOptions.find((s) => s.id === scenarioId)?.name : undefined
    const { data: selectedObject, isLoading: loadingObject } = useObject(selectedObjectID ?? null)
    const { data: objectScenarios = [] } = useScenarios(selectedObjectID ?? null)
    const defaultScenarioId = objectScenarios.find((s) => s.isDefault)?.id ?? null
    const { data: rtoBreakdown = [], isLoading: isLoadingRtoBreakdown } = useRolledUpRTO(selectedObjectID ?? null, scenarioName)
    const { data: rpoBreakdown = [], isLoading: isLoadingRpoBreakdown } = useRolledUpRPO(selectedObjectID ?? null, scenarioName)

    const { data: recoveryObjectsData, isLoading: loadingObjects } = useImpactRecoveryObjects(setId ?? null, {
        search: recoveryObjectsSearch || undefined,
        location: locationId,
        team: teamId,
    })
    const { data: locationOptions = [] } = useImpactLocationOptions(setId ?? null)
    const { data: teamOptions = [] } = useImpactTeamOptions(setId ?? null)
    const { data: statusData, isLoading: loadingStatus } = useImpactRecoveryObjectStatus(setId ?? null, {
        search: statusSearch || undefined,
        filter: statusFilter,
        scenario: scenarioId,
    })
    const updateStatusMutation = useUpdateImpactRecoveryObjectStatus(setId ?? null)
    const updateRecoveryObjectMutation = useUpdateImpactRecoveryObject(setId ?? null)

    const recoveryObjects = Array.isArray(recoveryObjectsData) ? recoveryObjectsData : []

    useEffect(() => {
        if (editingIssueId != null && issueEditRef.current) {
            issueEditRef.current.focus()
            issueEditRef.current.select()
        }
    }, [editingIssueId])

    const handleStartEditIssue = useCallback((row: { id: number; issueDescription: string }) => {
        setEditingIssueId(row.id)
        setEditingIssueValue(row.issueDescription ?? '')
    }, [])

    const handleSaveIssue = useCallback(
        (id: number, originalIssueDescription: string) => {
            if (editingIssueId !== id) return
            const value = editingIssueValue.trim()
            const original = (originalIssueDescription ?? '').trim()
            if (value === original) {
                setEditingIssueId(null)
                setEditingIssueValue('')
                return
            }
            updateRecoveryObjectMutation.mutate(
                { id, issueDescription: value },
                {
                    onSettled: () => {
                        setEditingIssueId(null)
                        setEditingIssueValue('')
                    },
                }
            )
        },
        [editingIssueId, editingIssueValue, updateRecoveryObjectMutation]
    )
    const statusRows = statusData?.data ?? []
    const scenariosAffected = statusData?.scenariosAffected ?? 0
    const affectedScenarioIds = new Set(statusData?.affectedScenarioIds ?? [])

    const handleToggle = useCallback(
        (id: number, currentStatus: number) => {
            const nextStatus = currentStatus === 2 ? 4 : 2
            updateStatusMutation.mutate({ id, objectStatus: nextStatus as 2 | 4 })
        },
        [updateStatusMutation]
    )

    const getStatusObjectIdCellClass = useCallback((analytics: number | null) => {
        if (analytics === 2) return 'bg-red-100 dark:bg-red-900/30'
        if (analytics === 3) return 'bg-amber-100 dark:bg-amber-900/30'
        return ''
    }, [])

    if (setId == null || setId <= 0) {
        return (
            <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.step5Description', 'Mark compromised objects identified during the technical checklist.')}
                </p>
                <p className="text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.impactAssessmentRequiresSet', 'Impact assessment is available in Recovery Set context.')}
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-4 w-full max-w-full">
            <div>
                <h1 className="text-xl font-semibold">{t('recoverRequests.mobilisation.impactAssessment', 'Impact Assessment')}</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.step5Description', 'Mark compromised objects identified during the technical checklist.')}
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-x-4 gap-y-4 w-full min-w-0" style={{ gridTemplateRows: 'auto auto auto 1fr' }}>
                {/* Row 1: right only = dropdown + circle (above headings) */}
                <div className="min-w-0" aria-hidden />
                <div className="flex flex-wrap items-center gap-4 min-h-[2.5rem] min-w-0">
                    <div className={cn('rounded-md', scenariosAffected > 0 && 'animate-scenarios-dropdown-border-pulse')}>
                        <Select value={scenarioId != null ? String(scenarioId) : 'all'} onValueChange={(v) => setScenarioId(v === 'all' ? null : parseInt(v, 10))}>
                            <SelectTrigger className="w-[280px]">
                                <SelectValue placeholder={t('recoverRequests.mobilisation.scenario', 'Scenario')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('recoverRequests.mobilisation.filterAll', 'All')}</SelectItem>
                                {scenarioOptions.map((s) => (
                                    <SelectItem
                                        key={s.id}
                                        value={String(s.id)}
                                        className={affectedScenarioIds.has(s.id) ? 'bg-amber-100 dark:bg-amber-900/40 focus:bg-amber-100 dark:focus:bg-amber-900/40' : undefined}
                                    >
                                        {s.name || `Scenario ${s.id}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div
                        className={cn(
                            'flex items-center justify-center w-16 h-16 rounded-full shrink-0 font-bold text-xl border-2',
                            scenariosAffected > 0
                                ? 'border-amber-500 bg-amber-500/20 text-amber-700 dark:text-amber-400 animate-scenarios-affected-pulse'
                                : 'border-muted-foreground/40 bg-muted text-muted-foreground'
                        )}
                        title={t('recoverRequests.mobilisation.scenariosAffected', 'Scenarios Affected')}
                        aria-label={t('recoverRequests.mobilisation.scenariosAffected', 'Scenarios Affected') + ': ' + scenariosAffected}
                    >
                        {scenariosAffected}
                    </div>
                </div>
                {/* Row 2: both headings on the same line */}
                <h2 className="text-lg font-semibold min-w-0">{t('recoverRequests.mobilisation.impactRecoveryObjects', 'Recovery Objects')}</h2>
                <h2 className="text-lg font-semibold min-w-0">{t('recoverRequests.mobilisation.impactRecoveryObjectStatus', 'Recovery Object Status')}</h2>
                {/* Row 3: both search fields on the same line, above the tables; left has search + Location + Team filters */}
                <div className="flex flex-wrap items-center gap-3 min-h-[2.5rem] min-w-0">
                    <div className="relative min-w-0 max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder={t('recoverRequests.mobilisation.searchRecoveryObjects', 'Search recovery objects...')}
                            value={recoveryObjectsSearch}
                            onChange={(e) => setRecoveryObjectsSearch(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <Select value={locationId != null ? String(locationId) : 'all'} onValueChange={(v) => setLocationId(v === 'all' ? null : parseInt(v, 10))}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder={t('recoverRequests.mobilisation.location', 'Location')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('recoverRequests.mobilisation.filterAll', 'All')}</SelectItem>
                            {locationOptions.map((loc) => (
                                <SelectItem key={loc.id} value={String(loc.id)}>{loc.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={teamId != null ? String(teamId) : 'all'} onValueChange={(v) => setTeamId(v === 'all' ? null : parseInt(v, 10))}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder={t('recoverRequests.mobilisation.team', 'Team')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('recoverRequests.mobilisation.filterAll', 'All')}</SelectItem>
                            {teamOptions.map((team) => (
                                <SelectItem key={team.id} value={String(team.id)}>{team.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-wrap items-center gap-3 min-h-[2.5rem] min-w-0">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder={t('recoverRequests.mobilisation.searchStatus', 'Search status...')}
                            value={statusSearch}
                            onChange={(e) => setStatusSearch(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{t('recoverRequests.mobilisation.filterBy', 'Filter by')}:</span>
                        <div className="flex rounded-md border border-border bg-muted/30 p-0.5">
                            {(['all', 'services', 'amber', 'amber-services'] as const).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setStatusFilter(f)}
                                    className={cn(
                                        'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                                        statusFilter === f ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {f === 'all' && t('recoverRequests.mobilisation.filterAll', 'All')}
                                    {f === 'services' && t('recoverRequests.mobilisation.filterServices', 'Services')}
                                    {f === 'amber' && t('recoverRequests.mobilisation.filterAmber', 'Amber')}
                                    {f === 'amber-services' && t('recoverRequests.mobilisation.filterAmberServices', 'Amber Services')}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Row 4: both tables so tops align */}
                <div className="rounded-md border overflow-auto min-h-[200px] min-w-0">
                    {loadingObjects ? (
                        <p className="p-4 text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                    ) : (
                        <table
                            className="w-full caption-bottom text-sm border-collapse"
                            style={{ tableLayout: 'fixed', minWidth: 800 }}
                        >
                            <colgroup>
                                <col style={{ width: '160px' }} />
                                <col />
                                <col style={{ width: '300px' }} />
                                <col style={{ width: '80px' }} />
                            </colgroup>
                            <thead>
                                <TableRow className="bg-table-header border-b border-table-border">
                                    <TableHead className="h-12 font-semibold text-foreground text-xs px-4">{t('recoverRequests.mobilisation.objectId', 'Object ID')}</TableHead>
                                    <TableHead className="h-12 font-semibold text-foreground text-xs px-4">{t('recoverRequests.mobilisation.description', 'Description')}</TableHead>
                                    <TableHead className="h-12 font-semibold text-foreground text-xs px-4">{t('recoverRequests.mobilisation.issueDescription', 'Issue Description')}</TableHead>
                                    <TableHead className="h-12 font-semibold text-foreground text-xs text-left pl-2 pr-2 w-20">{t('recoverRequests.mobilisation.impactToggle', 'Issue')}</TableHead>
                                </TableRow>
                            </thead>
                            <tbody>
                                {recoveryObjects.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8 text-xs">
                                            {t('recoverRequests.mobilisation.noRecoveryObjects', 'No recovery objects.')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    recoveryObjects.map((row) => (
                                        <TableRow key={row.id} className={cn(row.objectStatus === 2 && 'bg-red-100 dark:bg-red-900/30')}>
                                            <TableCell
                                                className="text-xs break-words align-top py-2 px-4 cursor-pointer hover:bg-muted/50"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => { setModalSource('recovery-objects'); setSelectedObjectID(row.objectID) }}
                                                onKeyDown={(e) => e.key === 'Enter' && (setModalSource('recovery-objects'), setSelectedObjectID(row.objectID))}
                                            >
                                                {row.objectID}
                                            </TableCell>
                                            <TableCell
                                                className="text-xs min-w-0 break-words align-top py-2 px-4 cursor-pointer hover:bg-muted/50"
                                                title={row.description}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => { setModalSource('recovery-objects'); setSelectedObjectID(row.objectID) }}
                                                onKeyDown={(e) => e.key === 'Enter' && (setModalSource('recovery-objects'), setSelectedObjectID(row.objectID))}
                                            >
                                                {row.description || '—'}
                                            </TableCell>
                                            <TableCell
                                                className="text-xs align-top py-2 px-4"
                                                onClick={() => editingIssueId !== row.id && handleStartEditIssue(row)}
                                            >
                                                {editingIssueId === row.id ? (
                                                    <Textarea
                                                        ref={issueEditRef}
                                                        rows={1}
                                                        value={editingIssueValue}
                                                        onChange={(e) => setEditingIssueValue(e.target.value)}
                                                        onBlur={() => handleSaveIssue(row.id, row.issueDescription ?? '')}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault()
                                                                handleSaveIssue(row.id, row.issueDescription ?? '')
                                                            }
                                                            if (e.key === 'Escape') {
                                                                setEditingIssueId(null)
                                                                setEditingIssueValue(row.issueDescription ?? '')
                                                            }
                                                        }}
                                                        className="min-h-[1.5rem] w-full text-xs resize-y border-muted-foreground/30 overflow-auto py-0.5 px-2"
                                                        placeholder={t('recoverRequests.mobilisation.issueDescription', 'Issue Description')}
                                                    />
                                                ) : (
                                                    <span className="block min-h-[1.5rem] break-words cursor-text rounded border border-border bg-background px-2 py-1 text-left whitespace-pre-wrap">
                                                        {row.issueDescription || ''}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="w-20 pl-2 pr-2 text-left align-top py-2 px-4">
                                                <Switch
                                                    checked={row.objectStatus === 2}
                                                    onCheckedChange={() => handleToggle(row.id, row.objectStatus)}
                                                    disabled={updateStatusMutation.isPending}
                                                    className="data-[state=checked]:bg-red-600"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="rounded-md border overflow-auto min-h-[200px] min-w-0">
                        {loadingStatus ? (
                            <p className="p-4 text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                        ) : (
                            <Table className="w-full table-fixed">
                                <TableHeader>
                                    <TableRow className="bg-table-header border-b border-table-border">
                                        <TableHead className="font-semibold text-foreground w-[160px] max-w-[200px] text-xs">{t('recoverRequests.mobilisation.objectId', 'Object ID')}</TableHead>
                                        <TableHead className="font-semibold text-foreground min-w-0 text-xs">{t('recoverRequests.mobilisation.description', 'Description')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {statusRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={2} className="text-center text-muted-foreground py-8 text-xs">
                                                {t('recoverRequests.mobilisation.noRecoveryObjectStatus', 'No recovery object status.')}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        statusRows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className="h-12 cursor-pointer hover:bg-muted/50"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => { setModalSource('recovery-object-status'); setSelectedObjectID(row.objectID) }}
                                                onKeyDown={(e) => e.key === 'Enter' && (setModalSource('recovery-object-status'), setSelectedObjectID(row.objectID))}
                                            >
                                                <TableCell className={cn('text-xs w-[160px] max-w-[200px] break-words align-middle py-2 px-4', getStatusObjectIdCellClass(row.analytics))}>{row.objectID}</TableCell>
                                                <TableCell className={cn('text-xs min-w-0 break-words align-middle py-2 px-4', getStatusObjectIdCellClass(row.analytics))}>{row.description || '—'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </div>
            </div>
            <Dialog open={selectedObjectID != null} onOpenChange={(open) => !open && (setSelectedObjectID(null), setModalSource(null))}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('recoverRequests.mobilisation.objectInformation', 'Object Information')}</DialogTitle>
                        <DialogDescription className="sr-only">
                            {t('recoverRequests.mobilisation.objectInformationDescription', 'Details for the selected recovery object.')}
                        </DialogDescription>
                    </DialogHeader>
                    {loadingObject ? (
                        <p className="text-sm text-muted-foreground py-4">{t('common.loading', 'Loading...')}</p>
                    ) : selectedObject ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-primary w-28 shrink-0">{t('objects.objectID', 'Object ID')}</span>
                                    <p className="text-base font-bold">{selectedObject.objectID || '-'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">{t('objects.objectDescription', 'Description')}</span>
                                    <p className="text-sm text-foreground">{selectedObject.objectDescription || '-'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                        <Layers className="h-3 w-3" />
                                        {t('objects.layerID', 'Layer ID')}
                                    </span>
                                    <p className="text-sm text-foreground">
                                        {selectedObject.layerID != null
                                            ? `${selectedObject.layerID}${selectedObject.layerDescription ? ` - ${selectedObject.layerDescription}` : ''}`
                                            : '-'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {t('objects.objectLocation', 'Location')}
                                    </span>
                                    <p className="text-sm text-foreground">{selectedObject.objectLocation || '-'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">{t('objects.objectStatus', 'Object Status')}</span>
                                    <ObjectStatusIndicator status={selectedObject.objectStatus != null ? Number(selectedObject.objectStatus) : null} />
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">{t('objects.applicationEncryption', 'App Encryption')}</span>
                                    {selectedObject.applicationEncryption === 1 || selectedObject.applicationEncryption === '1' ? (
                                        <img src="/images/ApplicationEncryption.png" alt="Application Encryption" className="h-4 w-4" />
                                    ) : (
                                        <span className="text-muted-foreground">-</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium text-muted-foreground w-28 shrink-0">{t('objects.datastore', 'Datastore')}</span>
                                    {selectedObject.datastore === 1 ? (
                                        <img src="/images/database.png" alt="Datastore" className="h-4 w-4" />
                                    ) : (
                                        <span className="text-muted-foreground">-</span>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3 flex flex-col items-end">
                                {/* Rolled Up RPO / RTO - right of modal, same size as object screen, clickable for breakdown modals */}
                                <button
                                    type="button"
                                    onClick={() => setRpoBreakdownOpen(true)}
                                    className="flex flex-col text-right focus:outline-none rounded w-fit"
                                    title={t('objects.rolledUpRPO', 'Rolled Up RPO')}
                                >
                                    <span className="text-[9px] font-medium text-muted-foreground mb-0.5">{t('objects.rolledUpRPO', 'Rolled Up RPO')}</span>
                                    <div className="border rounded px-2 py-1.5 text-right min-w-[130px] border-primary bg-primary/5">
                                        <span className="text-xs font-medium text-primary">
                                            {selectedObject.rolledUpRPO != null && selectedObject.rolledUpRPO !== undefined ? formatSecondsToDHM(selectedObject.rolledUpRPO) : '-'}
                                        </span>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRtoBreakdownOpen(true)}
                                    className="flex flex-col text-right focus:outline-none rounded w-fit"
                                    title={t('objects.rolledUpRTO', 'Rolled Up RTO')}
                                >
                                    <span className="text-[9px] font-medium text-muted-foreground mb-0.5">{t('objects.rolledUpRTO', 'Rolled Up RTO')}</span>
                                    <div className="border rounded px-2 py-1.5 text-right min-w-[130px] border-primary bg-primary/5">
                                        <span className="text-xs font-medium text-primary">
                                            {selectedObject.rolledUpRTO != null && selectedObject.rolledUpRTO !== undefined ? formatSecondsToDHM(selectedObject.rolledUpRTO) : '-'}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    ) : selectedObjectID ? (
                        <p className="text-sm text-muted-foreground py-4">{t('common.notFound', 'Not found.')}</p>
                    ) : null}
                    {selectedObjectID != null && (
                        <DialogFooter className="flex flex-row justify-between border-t pt-4 mt-4 [&>*]:m-0">
                            <Button variant="outline" onClick={() => { setSelectedObjectID(null); setModalSource(null) }} className="order-1">
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            {selectedObject?.objectID ? (
                                <Button
                                    onClick={() => {
                                        const objectId = selectedObject.objectID
                                        const scenarioToUse = modalSource === 'recovery-object-status'
                                            ? (scenarioId != null ? String(scenarioId) : null)
                                            : (defaultScenarioId != null ? String(defaultScenarioId) : null)
                                        try {
                                            if (scenarioToUse) localStorage.setItem('selectedScenario', scenarioToUse)
                                            else localStorage.removeItem('selectedScenario')
                                        } catch (_) {}
                                        setSelectedObjectID(null)
                                        setModalSource(null)
                                        navigate(`/objects/${encodeURIComponent(objectId)}`)
                                    }}
                                    className="order-2"
                                >
                                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                                    {selectedObject.objectService === 'Service'
                                        ? t('recoverRequests.mobilisation.goToService', 'Go To Service')
                                        : t('recoverRequests.mobilisation.goToObject', 'Go To Object')}
                                </Button>
                            ) : null}
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
            {/* Rolled Up RTO Breakdown Dialog - same as object screen */}
            <Dialog open={rtoBreakdownOpen} onOpenChange={setRtoBreakdownOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRTO', 'Rolled Up RTO')}, {selectedObject?.objectID ?? selectedObjectID ?? ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRTO', 'Rolled Up RTO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-end pr-3">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <span>{t('objects.rolledUpRTO', 'Rolled Up RTO')}:</span>
                                <span className="font-semibold text-foreground">
                                    {selectedObject?.rolledUpRTO != null && selectedObject?.rolledUpRTO !== undefined ? formatSecondsToDHM(selectedObject.rolledUpRTO) : '-'}
                                </span>
                            </div>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-[150px_1fr_80px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                <span>Object ID</span>
                                <span>Description</span>
                                <span>Source Layer</span>
                                <span className="text-right">RTO</span>
                            </div>
                            <div className="max-h-64 overflow-auto divide-y divide-border">
                                {isLoadingRtoBreakdown ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject', 'Loading...')}</div>
                                ) : rtoBreakdown.length === 0 ? (
                                    <div className="p-3 text-sm text-muted-foreground">No data</div>
                                ) : (
                                    rtoBreakdown.map((row, idx) => (
                                        <div key={`${row.objectID}-${row.layerID}-${idx}`} className="grid grid-cols-[150px_1fr_80px_100px] px-3 py-2 text-sm gap-3">
                                            <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                            <span className="truncate" title={row.objectDescription}>{row.objectDescription || '-'}</span>
                                            <span>{row.layerID ?? '-'}</span>
                                            <span className="text-right">{row.rto != null && row.rto !== undefined ? formatSecondsToDHM(row.rto) : '-'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Rolled Up RPO Breakdown Dialog - same as object screen */}
            <Dialog open={rpoBreakdownOpen} onOpenChange={setRpoBreakdownOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRPO', 'Rolled Up RPO')} {selectedObject?.objectID ?? selectedObjectID ?? ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRPO', 'Rolled Up RPO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-end pr-3">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <span>{t('objects.rolledUpRPO', 'Rolled Up RPO')}:</span>
                                <span className="font-semibold text-foreground">
                                    {selectedObject?.rolledUpRPO != null && selectedObject?.rolledUpRPO !== undefined ? formatSecondsToDHM(selectedObject.rolledUpRPO) : '-'}
                                </span>
                            </div>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-[150px_1fr_100px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                <span>Object ID</span>
                                <span>Description</span>
                                <span className="text-center">Source Layer</span>
                                <span className="text-right">RPO</span>
                            </div>
                            <div className="max-h-64 overflow-auto divide-y divide-border">
                                {isLoadingRpoBreakdown ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject', 'Loading...')}</div>
                                ) : rpoBreakdown.length === 0 ? (
                                    <div className="p-3 text-sm text-muted-foreground">No data</div>
                                ) : (
                                    rpoBreakdown.map((row, idx) => (
                                        <div key={`${row.objectID}-${idx}`} className="grid grid-cols-[150px_1fr_100px_100px] px-3 py-2 text-sm gap-3">
                                            <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                            <span className="truncate" title={row.objectDescription}>{row.objectDescription || '-'}</span>
                                            <span className="text-center">{row.sourceLayer ?? '-'}</span>
                                            <span className="text-right">{row.rpo != null && row.rpo !== undefined ? formatSecondsToDHM(row.rpo) : '-'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
